import type { TrackingProps } from '@seek/tealium-adapter/lib-types/types';
import { useTranslations } from '@vocab/react';
import { Bleed, Box, Drawer, Stack, IconOverflow } from 'braid-design-system';
import React from 'react';

import { EVENT_NAME_JOB_ACTION_MENU_PRESSED } from 'src/App/constants/tracking';

import { IconButtonWithoutTooltip } from '../../IconButtonLink/IconButtonWithoutTooltip';
import translations from '../.vocab';
import type { JobAction } from '../type';
import type { JobActionMenuOption } from '../useJobActionMenuOptions';

import { IconRow } from './IconRow';

interface JobActionMobileProps {
  disabled?: boolean;
  eventAttributesProps?: TrackingProps;
  jobActionMenuOptions: JobActionMenuOption[];
  onAction: (jobAction: JobAction) => void;
  id: string;
}

export const JobActionMobile: React.FC<
  React.PropsWithChildren<JobActionMobileProps>
> = ({
  disabled,
  eventAttributesProps,
  jobActionMenuOptions,
  onAction,
  id,
}) => {
  const { t } = useTranslations(translations);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  return (
    <Box>
      <IconButtonWithoutTooltip
        active={drawerOpen}
        disabled={disabled}
        eventAttributes={eventAttributesProps}
        eventName={EVENT_NAME_JOB_ACTION_MENU_PRESSED}
        hoverBackground="formAccentSoft"
        icon={<IconOverflow titleId={`${id}-menu`} title={t('Mobile menu')} />}
        onClick={() => setDrawerOpen(true)}
      />

      <Drawer
        id="job-actions"
        title={t('Menu')}
        open={drawerOpen}
        width="small"
        onClose={() => setDrawerOpen(false)}
      >
        <Stack space="medium">
          <Bleed horizontal={{ mobile: 'xsmall', tablet: 'small' }}>
            {jobActionMenuOptions.map((option) => (
              <IconRow
                key={option.value}
                icon={option.icon}
                label={option.label}
                onClick={() => {
                  setDrawerOpen(false);
                  onAction(option.value);
                }}
                inactive={option.inactive}
                inactiveDescription={option.inactiveDescription}
                content={option.content}
              />
            ))}
          </Bleed>
        </Stack>
      </Drawer>
    </Box>
  );
};
